/** Origin prefix extracted from the component's tag name (e.g. "app", "mat", "ngx"). */
export type ComponentOrigin = string;

export interface ComponentNode {
  /** Unique id for tracking expand/collapse state. */
  id: string;
  /** Angular component class name (constructor.name). */
  name: string;
  /** The host DOM element. */
  element: HTMLElement;
  /** Direct child components. */
  children: ComponentNode[];
  /** Depth in the tree (root = 0). */
  depth: number;
  /** Origin prefix derived from the element tag (e.g. "app", "mat", "ngx"). */
  origin: ComponentOrigin;
}

/**
 * Extract origin prefix for a component.
 *
 * 1. If the tag has a dash (e.g. `app-root`, `mat-icon`), use everything
 *    before the first dash.
 * 2. Otherwise the component lives on a native element via an attribute
 *    selector (e.g. `<button mat-button>`). Check the element's attributes
 *    for known Angular library prefixes.
 * 3. Fall back to the PascalCase prefix of the component class name
 *    (`MatButton` → `mat`).
 * 4. If nothing matches, return `'other'` instead of the raw tag name.
 */
export function extractOrigin(tagName: string, className?: string, element?: HTMLElement): ComponentOrigin {
  const lower = tagName.toLowerCase();
  const dashIdx = lower.indexOf('-');
  if (dashIdx > 0) return lower.substring(0, dashIdx);

  // Native-element host — check attributes for known Angular library selectors.
  // e.g. <button mat-button>, <input matInput>, <div cdkDrag>, <a routerLink>
  if (element) {
    const attrs = element.getAttributeNames();
    for (const attr of attrs) {
      // mat-button, mat-raised-button, matInput, matTooltip, etc.
      if (/^mat[-A-Z]/.test(attr) || attr === 'mat-button' || attr === 'mat-icon-button') return 'mat';
      // cdk-drag, cdkDropList, etc.
      if (/^cdk[-A-Z]/.test(attr)) return 'cdk';
      // ngx-*
      if (/^ngx[-A-Z]/.test(attr)) return 'ngx';
      // router directives → angular
      if (/^router/.test(attr)) return 'router';
      // formControl, formGroup, ngModel → angular forms
      if (/^(formControl|formGroup|ngModel|ngForm)/.test(attr)) return 'forms';
    }
  }

  // Derive from class name prefix (PascalCase: MatButton → mat).
  if (className) {
    const match = className.match(/^([A-Z][a-z]+)/);
    if (match) return match[1].toLowerCase();
  }

  // Don't return the raw tag name ("a", "button", "div") as an origin.
  return 'other';
}
